import { Table, Select, Space, Tag, Descriptions, Typography, Alert } from 'antd'
import { translate } from '../../../core'
import { dateDisplay, dateFormatSlashed } from '../../../core/until'
import { InputCard } from '../../inventory/component/InputCard'
import { InfoTooltip } from '../../../core/component'
import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { GetOnlinePayments } from '../graphql/online-payment.graphql'
import { OnlinePayment, OnlinePaymentStatus } from '../type'
import { DollarOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

export const OnlinePaymentList = () => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [statusFilter, setStatusFilter] = useState<OnlinePaymentStatus | undefined>(undefined)

  const { data, loading } = useQuery(GetOnlinePayments, {
    variables: {
      request: {
        take: pageSize,
        skip: (currentPage - 1) * pageSize,
        status: statusFilter,
      },
    },
  })

  const getStatusColor = (status: OnlinePaymentStatus) => {
    switch (status) {
      case OnlinePaymentStatus.Completed:
        return 'green'
      case OnlinePaymentStatus.Pending:
        return 'orange'
      case OnlinePaymentStatus.Cancelled:
        return 'red'
      case OnlinePaymentStatus.Failed:
        return 'red'
      default:
        return 'default'
    }
  }

  return (
    <InputCard contained>
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Space>
          <DollarOutlined style={{ fontSize: 20, color: '#a3bde3' }} />
          <strong>{translate('online', 'payment')}</strong>
          <InfoTooltip contentKey='onlinePayment.list' placement='right' />
        </Space>
        <Select
          placeholder={translate('filter', 'by', 'status')}
          allowClear
          style={{ width: 200 }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
        >
          {Object.entries(OnlinePaymentStatus).map(([key, value]) => (
            <Select.Option value={value}>
              {translate(key)}
            </Select.Option>
          ))}
        </Select>
      </Space>

      <Table<OnlinePayment>
        loading={loading}
        columns={[
          {
            dataIndex: 'id',
            title: translate('id'),
            width: 100,
          },
          {
            dataIndex: 'createdAt',
            title: translate('created_date'),
            width: 200,
            render: (value) => dateDisplay(value, dateFormatSlashed),
          },
          {
            dataIndex: 'status',
            title: translate('status'),
            width: 150,
            render: (value: OnlinePaymentStatus) => (
              <Tag color={getStatusColor(value)}>
                {value}
              </Tag>
            ),
          },
          {
            dataIndex: 'provider',
            title: translate('provider'),
            width: 150,
          },
          {
            dataIndex: 'email',
            title: translate('email'),
            width: 200,
          },
          {
            dataIndex: 'payable',
            title: translate('amount'),
            width: 150,
            render: (value) =>
              value ? `$${value.toFixed(2)}` : '-',
          },
          {
            dataIndex: 'reference',
            title: translate('reference'),
            width: 200,
          },
        ]}
        dataSource={
          data?.onlinePaymentsFind.data.map((it) => ({
            key: it.id.toString(),
            ...it,
          })) ?? []
        }
        expandable={{
          expandedRowRender: (record) => {
            if (!record.checkout) {
              return <div>{translate('no', 'checkout', 'details')}</div>
            }
            const checkout = record.checkout
            return (
              <div>
                <Typography.Title level={5} style={{ marginBottom: 16 }}>
                  {translate('checkout')}
                </Typography.Title>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label={translate('id')}>
                    {checkout.id}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('customer', 'id')}>
                    {checkout.customerId ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('phone')}>
                    {checkout.phone ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('email')}>
                    {checkout.email ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('freight')}>
                    {checkout.freight ? `$${checkout.freight.toFixed(2)}` : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('tax')}>
                    {checkout.tax ? `$${checkout.tax.toFixed(2)}` : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('payable')}>
                    {checkout.payable ? `$${checkout.payable.toFixed(2)}` : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('shipping_method')}>
                    {checkout.shippingMethod ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('special', 'instructions')} span={2}>
                    {checkout.specialInstructions ?? '-'}
                  </Descriptions.Item>
                </Descriptions>
                {record.status === OnlinePaymentStatus.Failed && (
                  <div style={{ marginTop: 24 }}>
                    <Typography.Title level={5} style={{ marginBottom: 8, color: '#ff4d4f' }}>
                      {translate('error', 'message')}
                    </Typography.Title>
                    <Alert
                      message={record.errorMessage || translate('no', 'error', 'message', 'available')}
                      type="error"
                      showIcon
                      style={{ marginTop: 8 }}
                    />
                  </div>
                )}
              </div>
            )
          },
        }}
        pagination={{
          total: data?.onlinePaymentsFind.count,
          current: currentPage,
          position: ['bottomCenter'],
          pageSize: pageSize,
          showSizeChanger: true,
          showTotal: (total) => translate('total', total.toString()),
        }}
        onChange={(e) => {
          if (e.current) setCurrentPage(e.current)
          if (e.pageSize) setPageSize(e.pageSize)
        }}
      />
    </InputCard>
  )
}

